// utils/DataService.js

import {
  getRetreatData,
  saveGroup,
  updateGroupStatus,
  getNewRegistrationStatus,
  getRegistrationCounts,
} from "../../services/RetreatServices";
import {
  getEventById,
  getPublicEvents,
  validatePublicSpecialEventBooking,
} from "../../services/EventMasterService";
class EventSupport {
  static eventCache = new Map();

  static buildLegacyPaymentMethods(event) {
    const paymentModes = Array.isArray(event?.paymentModes)
      ? event.paymentModes
      : [];

    const methods = paymentModes
      .filter((mode) => mode?.isActive !== false)
      .map((mode) => String(mode?.displayName || mode?.modeType || "").trim())
      .filter(Boolean);

    return methods.join(", ");
  }

  static findLocalEventById(eventID) {
    const eventsPath =
      process.env.REACT_APP_EVENTS_SOURCE || "source/events.json";
    const events = require(`../../${eventsPath}`);
    return events.find((event) => String(event.id) === String(eventID));
  }

  static normalizeAgeGroups(ageGroups = []) {
    return (Array.isArray(ageGroups) ? ageGroups : [])
      .map((group) => ({
        ...group,
        minAge: Number(group?.minAge ?? 0),
        maxAge: Number(group?.maxAge ?? 120),
        rate: Number(group?.rate ?? 0),
        isActive: group?.isActive !== false,
      }))
      .filter((group) => group.isActive)
      .sort((a, b) => {
        if (a.minAge !== b.minAge) return a.minAge - b.minAge;
        return a.maxAge - b.maxAge;
      });
  }

  static getRateForAge(ageGroups = [], age, fallback = 0) {
    const numericAge = Number(age);
    if (!Number.isFinite(numericAge)) {
      return Number(fallback || 0);
    }

    const slab = ageGroups.find(
      (group) => numericAge >= group.minAge && numericAge <= group.maxAge
    );

    return Number(slab?.rate ?? fallback ?? 0);
  }

  static buildLegacyEventFromMaster(event) {
    const ageGroups = EventSupport.normalizeAgeGroups(event?.ageGroups);

    return {
      id: String(event?.eventID || ""),
      name: event?.name || "",
      reg_date: event?.regDate || "",
      reg_end_date: event?.regEndDate || "",
      start_date: event?.startDate || "",
      end_date: event?.endDate || "",
      timings: event?.timings || "",
      status: event?.status || "Draft",
      short_description: event?.shortDescription || "",
      description: event?.description || "",
      location: event?.location || "",
      address: event?.address || "",
      map: event?.map || "",
      type: event?.type || "Retreat",
      wog: event?.wog || "",
      retreatEmail: event?.retreatEmail || "",
      contact: event?.contact || "",
      maxRegistrationCount: Number(event?.maxRegistrationCount ?? 0),
      paymentMethods: EventSupport.buildLegacyPaymentMethods(event),
      onlineRegRequired: Boolean(event?.onlineRegRequired),
      roomBasePrice: 0,
      ageGroups,
    };
  }

  // Fetch event data by ID (for example purposes, can be extended to call real API)
  static async fetchEventData(eventID) {
    console.log("Fetching event details for retreatID:", eventID);
    const cacheKey = String(eventID);

    if (EventSupport.eventCache.has(cacheKey)) {
      return EventSupport.eventCache.get(cacheKey);
    }

    // Prefer model-backed event details so payment modes come from event_payment_modes.
    try {
      const response = await getEventById(eventID);
      const masterEvent = response?.event;
      if (masterEvent && String(masterEvent?.eventID) === String(eventID)) {
        const mappedEvent = EventSupport.buildLegacyEventFromMaster(masterEvent);
        EventSupport.eventCache.set(cacheKey, mappedEvent);
        console.log("Retreat details found in Event Master API by ID:", mappedEvent);
        return mappedEvent;
      }
    } catch (error) {
      console.warn("Event Master API by ID lookup failed. Falling back:", error?.message || error);
    }

    try {
      const response = await getPublicEvents();
      const events = Array.isArray(response?.events) ? response.events : [];
      const matched = events.find(
        (event) => String(event?.eventID) === String(eventID)
      );

      if (matched) {
        const mappedEvent = EventSupport.buildLegacyEventFromMaster(matched);
        EventSupport.eventCache.set(cacheKey, mappedEvent);
        console.log("Retreat details found in Event Master API:", mappedEvent);
        return mappedEvent;
      }
    } catch (error) {
      console.error("Error fetching event details from Event Master API:", error);
    }

    throw new Error("Retreat not found.");
  }

  // Fetch group data (for example purposes, can be extended to call real API)
  static async fetchGroupData(id, userEmail, userData, mode, registrationID) {
    try {
      console.log("EventSupport Fetching group data for userData:", userData);
      console.log("EventSupport Fetching group data for retreatID:", id);
      console.log("EventSupport Fetching group data for userEmail:", userEmail);
      console.log("EventSupport Fetching group data for mode:", mode);
      const retreatID = id;
      if (retreatID && userEmail && userData) {
        const response = await getRetreatData(
          retreatID,
          userEmail,
          userData,
          mode,
          registrationID
        );
        console.log("\n # EventSupport response fetched:", response);
        if (!response) {
          throw new Error("Failed to fetch group data");
        } else {
          let groupInfo = response;
          console.log(" ## EventSupport response :", response);
          if (groupInfo) {
            console.log(
              " ## 1 EventSupport numberOfParticipants :",
              groupInfo.numberOfParticipants
            );
            groupInfo.updatedBy = userData?.userID;
            if (!Array.isArray(groupInfo.groupMembers)) {
              groupInfo.groupMembers = [];
            }
            groupInfo.numberOfParticipants = EventSupport.calculateParticipants(
              groupInfo.groupMembers
            );
            console.log(
              " ## 2 EventSupport numberOfParticipants :",
              groupInfo.numberOfParticipants
            );
          }

          console.log(" ### EventSupport groupInfo return:", groupInfo);
          return groupInfo;
        }
      }
    } catch (error) {
      throw new Error(
        `Failed to fetch group data. Retry Again${error?.message ? ` (${error.message})` : ""}`
      );
    }
  }

  // Calculate total participants from group members
  static calculateParticipants(groupMembers) {
    if (!Array.isArray(groupMembers)) return 0;
    return groupMembers.filter((member) => !member.deleted).length;
  }

  static countMembersByAge(members) {
    const counts = {
      adults: 0,
      youngAdults: 0,
      teens: 0,
      children: 0,
      toddlers: 0,
    };

    members
      .filter((member) => !member.deleted)
      .forEach((person) => {
        if (person.age >= 19) {
          counts.adults++;
        } else if (person.age >= 16 && person.age <= 18) {
          counts.youngAdults++;
        } else if (person.age >= 13 && person.age <= 15) {
          counts.teens++;
          // } else if (person.age >= 7) {
        } else if (person.age >= 5 && person.age <= 12) {
          counts.children++;
        } else {
          counts.toddlers++;
        }
      });

    return counts;
  }
  static calculateAmount(groupInfo) {
    if (!groupInfo) return 0; // Return 0 if no data is available

    console.log("Calculating total amount...");
    const cacheKey = String(groupInfo.retreatID || "");
    let eventDetails = EventSupport.eventCache.get(cacheKey);
    if (!eventDetails) {
      eventDetails = EventSupport.findLocalEventById(groupInfo.retreatID);
    }
    if (!eventDetails) {
      return 0;
    }

    let updatedMembers = groupInfo.groupMembers
      ? groupInfo.groupMembers.filter((member) => !member.isDeleted)
      : [];

    console.log("Updated members:", updatedMembers);

    const rooms = Math.max(parseInt(groupInfo.rooms, 10) || 1, 1);
    const roomShare = groupInfo.shareRoom || false;

    const counts = EventSupport.countMembersByAge(updatedMembers);
    console.log("counts :", counts);

    const nOfAdults = counts.adults;
    const nOfTeens = counts.teens;
    const nOfChildren = counts.children;
    const nOfToddlers = counts.toddlers;
    const nOfYoungAdults = counts.youngAdults;

    const roomBasePrice = eventDetails.roomBasePrice || 0;
    const ageGroups = EventSupport.normalizeAgeGroups(eventDetails.ageGroups);
    const childRate = EventSupport.getRateForAge(ageGroups, 10, eventDetails.childRate || 0);
    const additionalAdultRate = EventSupport.getRateForAge(
      ageGroups,
      30,
      eventDetails.additionalAdultRate || eventDetails.adultRetreatRate || 0
    );

    console.log("Room base price:", roomBasePrice);

    let totalCost;

    if (ageGroups.length > 0) {
      totalCost = updatedMembers.reduce((sum, member) => {
        const memberRate = EventSupport.getRateForAge(ageGroups, member?.age, 0);
        return sum + memberRate;
      }, 0);
    } else {
      const teenRate = eventDetails.teenRate || 0;
      const youngAdultRate = eventDetails.youngAdultRate || 0;
      const adultRetreatRate = eventDetails.adultRetreatRate || 0;
      const toddlerRate = eventDetails.toddlerRate || 0;

      totalCost =
        nOfAdults * adultRetreatRate +
        nOfYoungAdults * youngAdultRate +
        nOfChildren * childRate +
        nOfTeens * teenRate +
        nOfToddlers * toddlerRate;
    }

    console.log("Total Retreat cost:", totalCost);
    if (eventDetails.type === "Residential" || eventDetails.type === "Leaders") {
      let totalRoomCost = roomBasePrice * rooms;
      if (roomShare) {
        totalRoomCost += EventSupport.calculateShareCost(
          nOfAdults,
          nOfYoungAdults,
          nOfTeens,
          nOfChildren,
          childRate,
          additionalAdultRate
        );
      } else {
        totalRoomCost += EventSupport.calculateNonShareCost(
          nOfAdults,
          nOfYoungAdults,
          nOfTeens,
          nOfChildren,
          childRate,
          additionalAdultRate
        );
      }
      totalCost = totalCost + totalRoomCost;
    }

    console.log("Total cost calculated (room & retreat):", totalCost);
    return Math.max(totalCost, 0);
  }

  static calculateShareCost(
    nOfAdults,
    nOfYoungAdults,
    nOfTeens,
    nOfChildren,
    childRate,
    additionalAdultRate
  ) {
    console.log("Calculating share cost...");
    if (nOfAdults === 1) {
      return nOfChildren > 0 ? (nOfChildren - 1) * childRate : 0;
    }
    return (nOfAdults - 2) * additionalAdultRate + nOfChildren * childRate;
  }

  static calculateNonShareCost(
    nOfAdults,
    nOfYoungAdults,
    nOfTeens,
    nOfChildren,
    childRate,
    additionalAdultRate
  ) {
    console.log("Calculating non-share cost...");
    let cost = 0;
    if (nOfAdults === 1) {
      cost += nOfChildren > 0 ? (nOfChildren - 1) * childRate : 0;
    } else {
      cost += (nOfAdults - 2) * additionalAdultRate + nOfChildren * childRate;
    }
    console.log("Non-share cost calculated:", cost);
    return cost;
  }

  static calculateAndApplyStripeFees(amount, eventData) {
    // Stripe fees: 2.9% + $0.30 per transaction
    const percentageFee = eventData.stripe_percentage
      ? parseFloat(eventData.stripe_percentage) / 100
      : 0.029;
    const fixedFee = eventData.stripe_fixed_amount
      ? parseFloat(eventData.stripe_fixed_amount)
      : 0.3;
    const totalFees = amount * percentageFee + fixedFee;
    const totalAmount = amount + totalFees;
    console.log("Original amount:", amount);
    console.log("Stripe fees:", totalFees);
    console.log("Total amount with fees:", totalAmount);

    return Math.ceil(totalAmount); // Round up needed
  }

  static async saveGroupData(groupData, saveMode) {
    //console.log("RetreatSupport Saving group data...", groupData);
    console.log("RetreatSupport saveMode:", saveMode);
    try {
      const response = await saveGroup(groupData, saveMode);
      console.log("Group data saved successfully:", response);
      return response;
    } catch (err) {
      console.error("Error saving group data:", err);
      console.log("err.response?.data :", err.response?.data);
      console.log("err.message:", err.message);
      //let error = err.response?.data ? err.response.data : err.message || "Error saving Registration";
      throw err;
    }
  }

  static async updateGroupStatus(groupData, status) {
    console.log("RetreatSupport updateGroupStatus group data...", groupData);
    console.log("RetreatSupport updateGroupStatus status...", status);
    try {
      const response = await updateGroupStatus(groupData, status);
      console.log("Group updateGroupStatus successfully:", response);
      return response;
    } catch (error) {
      console.error("Error saving group data:", error);
    }
  }

  static async canRegisterEvent(eventData) {
    const currentDate = new Date();
    const regDate = eventData.reg_date;
    const startDate = eventData.reg_end_date
      ? eventData.reg_end_date
      : eventData.start_date;
    const id = eventData.id;
    const maxCount = eventData.maxRegistrationCount;

    let canRegister =
      new Date(regDate) <= currentDate && currentDate <= new Date(startDate);

    if (canRegister) {
      try {
        const totalCount = await getRegistrationCounts(id);

        console.log("canRegister 1:", canRegister);
        canRegister = maxCount > 0 && totalCount < maxCount ? true : false;
        console.log("canRegister 2:", canRegister);
      } catch (error) {
        console.error("Error in canRegister:", error);
        return canRegister;
      }
    }
    return canRegister;
  }

  static async getNewRegistrationStatus(eventData) {
    const currentDate = new Date();
    const regDate = eventData.reg_date;
    const startDate = eventData.reg_end_date
      ? eventData.reg_end_date
      : eventData.start_date;
    const id = eventData.id;

    let canRegister =
      new Date(regDate) <= currentDate && currentDate <= new Date(startDate);

    if (canRegister) {
      try {
        return await getNewRegistrationStatus(id);
      } catch (error) {
        console.error("Error in getNewRegistrationStatus:", error);
        return "Draft"; // Default to "Draft" if there's an error
      }
    }
    return "Draft";
  }

  static async validateLeaderRegistrationAccess({ eventID, email, authCode }) {
    const normalizedEventID = String(eventID || "").trim();
    const normalizedEmail = String(email || "").trim().toLowerCase();
    const normalizedAuthCode = String(authCode || "").trim().toUpperCase();

    if (!normalizedEventID || !normalizedEmail || !normalizedAuthCode) {
      return {
        valid: false,
        message: "Invalid link. Missing id, email, or authCode.",
      };
    }

    try {
      const response = await validatePublicSpecialEventBooking(
        normalizedAuthCode,
        normalizedEventID,
        normalizedEmail
      );
      const booking = response?.booking;
      const bookingEventID = String(booking?.eventCode || "").trim();
      const bookingEmail = String(booking?.email || "").trim().toLowerCase();
      const bookingAuthCode = String(booking?.authCode || "").trim().toUpperCase();

      const isLeaderBooking =
        String(booking?.eventType || "").trim().toLowerCase() === "leaders";
      const isValid =
        Boolean(response?.valid) &&
        Boolean(booking) &&
        isLeaderBooking &&
        bookingEventID === normalizedEventID &&
        bookingEmail === normalizedEmail &&
        bookingAuthCode === normalizedAuthCode;

      if (!isValid) {
        return {
          valid: false,
          message: "Invalid leader registration link.",
        };
      }

      return {
        valid: true,
        booking,
      };
    } catch (error) {
      console.error("Leader registration access validation failed:", error);
      return {
        valid: false,
        message: "Unable to validate leader registration link.",
      };
    }
  }
}

export default EventSupport;
